import { ReactNode, useRef } from "react";
import usePortal from "react-useportal";

import { useRouter } from "next/router";

import { t } from "@lingui/macro";
import { Breadcrumb, Col, Layout, Row } from "antd";
import { isArray, isEmpty } from "lodash";
import styled from "styled-components";

import ToggleSidebar from "components/navigations/toggle-sidebar";
import { availableAccountMenuItems, availableMenus, availableUserMenuItems } from "components/navigations/utils";

import { useApplicationContext } from "contexts/app";

import useDebounce from "hooks/debounce";

import { isUUID } from "utils/uuid-utils";

const accessPermissionType = "can_access";

const Header = styled(Layout.Header)`
  padding: 0 24px 0 24px;
  background: ${(props) => props.theme.colorBgContainer};
  height: 48px;
  line-height: 48px;
`;

const Breadcrumbs = styled(Breadcrumb)`
  li {
    :hover {
      cursor: pointer;
    }

    :last-child {
      cursor: unset;
    }
  }
`;

export const ActionMenu = ({ menus }: { menus: ReactNode[] | ReactNode }) => {
  const { Portal } = usePortal({
    // @ts-ignore
    bindTo: document && document.getElementById("ported-action-menu"),
  });

  //delay rendering menus as we need App layout to complete rendering in order to target element with ID: `ported-action-menu`
  const debouncedMenus = useDebounce(menus, 100, true);

  if (!debouncedMenus) {
    return null;
  }

  return (
    <Portal>
      <Row gutter={8} align={"middle"}>
        {isArray(debouncedMenus)
          ? debouncedMenus.map((child, key) => {
              if (isEmpty(child)) {
                return;
              }

              return <Col key={key}>{child}</Col>;
            })
          : menus}
      </Row>
    </Portal>
  );
};

interface NavigationHeaderProps {
  label?: ReactNode;
}

const NavigationHeader = ({ label }: NavigationHeaderProps) => {
  const contentRef = useRef<any>();
  const { configuration } = useApplicationContext();
  const router = useRouter();
  const { name } = router.query;

  const canAccess = configuration?.account_permissions?.includes(accessPermissionType);
  const currentRoute = router.route;
  const paths = currentRoute.split("/").filter(Boolean);
  const configFeatures = configuration?.features;
  const first = paths[0];
  const second = `/${paths[0]}/${paths[1]}/`;
  const third = paths[2];

  /* First and second level menu navigations items */
  let menuItems: any = availableMenus(configFeatures);
  let submenu = menuItems.find((menu: { key: string }) => menu.key === `/${first}/`);
  let secondLevelMenuItems = submenu?.children || [];
  const secondLevelMenu = secondLevelMenuItems.find((m: { key: string }) => m.key === second);

  /* Breadcrumbs menu items */
  let breadCrumbs: any[] = [
    {
      title: submenu?.label,
      onClick: () => {
        router.push(secondLevelMenuItems?.[0]?.key);
      },
    },
  ];
  if (second) {
    breadCrumbs.push({
      title: secondLevelMenu?.label,
      onClick: () => {
        router.push(second);
      },
    });
  }
  if (third || label) {
    breadCrumbs.push({
      title: !isEmpty(label) ? label : isUUID(third) ? (name ? name : t`Edit`) : t`New`,
    });
  }
  if (first === "user") {
    const items = availableUserMenuItems(configuration?.features, !canAccess);

    breadCrumbs = [
      {
        title: items.find((item) => item.key === second)?.label,
        onClick: () => {
          router.push(second);
        },
      },
    ];

    if (third && label) {
      breadCrumbs.push({
        title: label,
      });
    }
  }
  if (first === "account") {
    const items = availableAccountMenuItems(configuration?.features, !canAccess);

    breadCrumbs = [
      {
        title: items.find((item) => item.key === second)?.label,
        onClick: () => {
          router.push(second);
        },
      },
    ];
    if (third && label) {
      breadCrumbs.push({
        title: label,
      });
    }
  }

  return (
    <Header ref={contentRef}>
      <Row align={"middle"} gutter={16} justify={"space-between"}>
        <Col flex={1}>
          <Row align={"middle"} gutter={16}>
            <Col>
              <ToggleSidebar />
            </Col>
            <Col>
              <Breadcrumbs items={breadCrumbs} />
            </Col>
          </Row>
        </Col>
        <Col>
          <Row justify={"end"}>
            <div id={"ported-action-menu"} />
          </Row>
        </Col>
      </Row>
    </Header>
  );
};

export default NavigationHeader;
